import type { DeleteCallOffMutationOptions } from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { queryKeysCallOff } from '../queryKeys';

import { deleteCallOff } from './deleteCallOff';

/**
 * Mutation key for call-off deletion.
 * Used with useMutationState to track pending delete actions.
 */
export const MUTATION_KEY_DELETE_CALLOFF = ['calloffs', 'delete'] as const;

/**
 * Custom hook for deleting a call-off.
 * Soft deletes the call-off by setting its status to DELETED.
 *
 * @param options - Optional mutation options.
 * @returns An object containing the mutation function and related properties.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/delete_calloff_calloffs__calloff_id__delete
 */
export const useMutationDeleteCallOff = (
  options?: DeleteCallOffMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UUID>({
    mutationFn: async (callOffId: UUID) => deleteCallOff(callOffId),
    mutationKey: MUTATION_KEY_DELETE_CALLOFF,
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getAll(),
      });
    },
    ...options,
  });
};
